import { type ComponentType } from 'react';
import { Facebook, Instagram, Youtube } from 'lucide-react';

/**
 * Shared social brand icons — the single source of truth for both the public
 * profile display (`dancer-socials.tsx`) and the `SocialHandleInput` editor.
 *
 * lucide-react ships Instagram / YouTube / Facebook, but NOT TikTok, Vimeo,
 * X (Twitter), Pinterest, or Snapchat, so those five are hand-rolled monogram
 * marks (`fill="currentColor"`, 24×24 viewBox) that visually match lucide's
 * weight. Keep every social glyph flowing through this module so a brand
 * refresh touches one file.
 */
export type SocialBrandIcon = ComponentType<{ className?: string }>;

export const TikTokIcon: SocialBrandIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M19.5 6.6a5.7 5.7 0 0 1-3.4-1.1V15a5.5 5.5 0 1 1-5.5-5.5c.3 0 .6 0 .9.1v2.7a2.8 2.8 0 1 0 1.9 2.6V3h2.6c.1 1.6 1.3 2.9 2.9 3.1h.6z" />
  </svg>
);

export const VimeoIcon: SocialBrandIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M22.4 7.1c-.1 1.9-1.4 4.5-4 7.8-2.7 3.5-4.9 5.2-6.8 5.2-1.1 0-2.1-1.1-2.9-3.2L7.1 10c-.6-2.1-1.2-3.2-1.9-3.2-.1 0-.6.3-1.5.9l-.9-1.2a107 107 0 0 1 2.8-2.5c1.3-1.1 2.2-1.7 2.9-1.8 1.5-.1 2.5.9 2.8 3.1.4 2.4.7 3.9.8 4.5.4 2 .9 3 1.4 3 .4 0 1-.6 1.8-1.9.8-1.3 1.2-2.2 1.3-2.9.2-1.5-.4-2.2-1.7-2.2-.6 0-1.2.1-1.9.4 1.2-3.9 3.5-5.8 6.9-5.7 2.5.1 3.6 1.7 3.5 4.8z" />
  </svg>
);

export const XIcon: SocialBrandIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const PinterestIcon: SocialBrandIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M12 2a10 10 0 0 0-3.6 19.3c-.1-.8-.2-2 0-2.9l1.3-5.6s-.3-.7-.3-1.7c0-1.5.9-2.7 2-2.7.9 0 1.4.7 1.4 1.5 0 .9-.6 2.3-.9 3.6-.3 1 .5 1.9 1.6 1.9 1.9 0 3.3-2 3.3-4.8 0-2.5-1.8-4.3-4.4-4.3-3 0-4.8 2.3-4.8 4.6 0 .9.3 1.9.8 2.5l.1.3-.4 1.5q-.1.2-.3.1c-1.2-.6-2-2.4-2-3.9 0-3.2 2.3-6.1 6.6-6.1 3.5 0 6.2 2.5 6.2 5.8 0 3.5-2.2 6.3-5.2 6.3-1 0-2-.6-2.3-1.2l-.6 2.3c-.2.9-.8 2-1.2 2.7q1.2.4 2.5.4a10 10 0 0 0 0-20z" />
  </svg>
);

export const SnapchatIcon: SocialBrandIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M12.2 2c2.8.1 4.9 1.4 6.1 4 .3.7.4 1.6.4 2.4 0 1.1 0 2.2-.1 3.3 0 .1 0 .2.2.2.4-.1 1.1-.3 1.5.2.3.4 0 .9-.4 1.1-.4.2-.9.4-1.3.5-.4.2-.5.4-.4.8.6 1.7 1.7 2.9 3.4 3.5.4.1.7.3.7.6 0 .4-.3.6-.6.7-.8.2-1.7.3-2.5.4-.2 0-.3.1-.4.4-.1.3-.2.7-.3 1-.1.4-.3.5-.7.4-.7-.1-1.4-.2-2.1-.2-.9 0-1.6.3-2.3 1-1.1 1-2.4 1.6-3.9 1.6s-2.8-.5-3.9-1.5c-.9-.9-1.9-1.3-3.2-1.1q-.4 0-.8.1c-1 .2-1.2.1-1.5-.9-.1-.4-.3-.6-.7-.7-.8-.1-1.5-.2-2.2-.4-.3-.1-.6-.3-.6-.7s.4-.5.7-.6c1.7-.5 2.8-1.7 3.5-3.4.2-.5.1-.7-.4-.9-.4-.1-.8-.3-1.2-.5-.4-.2-.7-.5-.5-1 .2-.4.7-.4 1.1-.3l.7.2c.1 0 .2-.1.1-.2v-.6c0-1 0-2.1.1-3.1.2-2.2 1.2-3.9 3.1-5C9.1 2.3 10.5 2 12.2 2z" />
  </svg>
);

/** Every supported social platform (matches the DB `*_handle` columns). */
export type SocialPlatform =
  | 'instagram'
  | 'tiktok'
  | 'facebook'
  | 'youtube'
  | 'vimeo'
  | 'x'
  | 'pinterest'
  | 'snapchat';

/**
 * platform → brand icon. lucide's `Instagram` / `Youtube` / `Facebook` accept
 * `className` (their other props are optional), so they satisfy the
 * `SocialBrandIcon` shape used across both surfaces.
 */
export const SOCIAL_BRAND_ICONS: Record<SocialPlatform, SocialBrandIcon> = {
  instagram: Instagram,
  tiktok: TikTokIcon,
  facebook: Facebook,
  youtube: Youtube,
  vimeo: VimeoIcon,
  x: XIcon,
  pinterest: PinterestIcon,
  snapchat: SnapchatIcon,
};
